<template>
  <section class="screen-projects" aria-labelledby="screen-projects-title">
    <header class="screen-projects__header">
      <h1 id="screen-projects-title">{{ projectType === 'scada2d' ? '2D组态' : '大屏可视化' }}</h1>
      <div class="screen-projects__header-actions">
        <button type="button" class="screen-projects__refresh" :disabled="listLoading" :aria-label="'刷新' + projectLabel + '列表'" :title="'刷新' + projectLabel + '列表'" @click="getList"><i class="el-icon-refresh" aria-hidden="true"></i></button>
        <el-form class="screen-projects__search" size="small" @submit.native.prevent="handleSearch">
          <el-input v-model="search.name" placeholder="搜索项目名称" :aria-label="'搜索' + projectLabel + '项目名称'" maxlength="128" clearable @clear="handleSearch"><el-button slot="append" icon="el-icon-search" native-type="submit" aria-label="搜索项目" /></el-input>
        </el-form>
        <template v-if="hash(`${permission}:add`)"><el-button v-if="canImportProject" size="small" icon="el-icon-upload2" aria-label="导入项目" :loading="importReading" :disabled="saving" @click="chooseImport">导入项目</el-button><el-button type="primary" size="small" icon="el-icon-plus" :aria-label="'新建' + projectLabel" @click="handleAdd">新建{{ projectLabel }}</el-button></template>
      </div>
      <input ref="importInput" type="file" accept=".json,application/json" hidden aria-label="选择大屏项目文件" @change="readImport" />
    </header>
    <div class="screen-projects__toolbar">
      <div ref="categories" class="screen-projects__categories" role="group" :aria-label="projectLabel + '分类筛选'" :aria-busy="categoryLoading ? 'true' : 'false'">
        <button type="button" :class="{ 'is-active': !search.catalogId }" :aria-pressed="String(!search.catalogId)" @click="selectCategory('')">全部</button>
        <button v-for="category in formCategories" :key="category.id" type="button" :class="{ 'is-active': search.catalogId === category.id }" :aria-pressed="String(search.catalogId === category.id)" :title="category.label" :disabled="categoryLoading || !!categoryError" @click="selectCategory(category.id)">{{ category.label }}</button>
        <button v-if="canCreateCategory" ref="addCategory" type="button" class="screen-projects__add-category" :disabled="categoryLoading || !!categoryError" :aria-label="'新增' + projectLabel + '分类'" title="新增分类" @click="openCategoryForm()"><i class="el-icon-plus" aria-hidden="true"></i></button>
        <span v-if="categoryLoading" class="screen-projects__category-loading" role="status">读取分类…</span>
      </div>
      <el-dropdown v-if="selectedCategory && (canCreateCategory || canDeleteCategory)" trigger="click" @command="handleCategoryCommand">
        <button ref="categoryActions" type="button" class="screen-projects__refresh" :aria-label="'分类操作：' + selectedCategory.label" title="当前分类操作" :disabled="categoryLoading || !!categoryError || categorySaving"><i class="el-icon-more" aria-hidden="true"></i></button>
        <el-dropdown-menu slot="dropdown" class="screen-project-menu" role="menu" :aria-label="selectedCategory.label + '分类操作'">
          <el-dropdown-item v-if="canCreateCategory" command="child" icon="el-icon-plus">新增子分类</el-dropdown-item>
          <el-dropdown-item v-if="canCreateCategory" command="edit" icon="el-icon-edit">编辑分类</el-dropdown-item>
          <el-dropdown-item v-if="canDeleteCategory" command="delete" icon="el-icon-delete" class="is-danger">删除分类</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <p v-if="categoryError" class="screen-projects__category-error" role="alert">{{ categoryError }} <button type="button" @click="getCategoryList">重试分类</button></p>
    <p v-if="copyUncertain && canCopyProject" class="screen-projects__category-error" role="alert">复制结果未确认，请先刷新列表核对副本，避免重复创建。<button type="button" :disabled="listLoading" @click="getList">刷新列表核对</button><button type="button" :disabled="!copyChecked || listLoading || !!listError" @click="acknowledgeCopyResult">已核对，解除复制保护</button></p>
    <div v-if="appliedName" class="screen-projects__scope" role="status">
      <span v-if="appliedCatalogId">当前分类：{{ appliedCategoryName }}</span>
      <span v-if="appliedName">名称：{{ appliedName }}</span>
      <button type="button" @click="handleReset">清除筛选</button>
      <span v-if="listLoading">正在加载{{ projectLabel }}列表…</span>
    </div>
    <div ref="results" class="screen-projects__results" role="region" :aria-label="projectLabel + '项目列表'" tabindex="0" :aria-busy="listLoading ? 'true' : 'false'" :class="{ 'is-loading': listLoading }">
      <p v-if="listLoading" class="screen-projects__loading-status" role="status" aria-live="polite"><span class="ui-loading-spinner ui-loading-spinner--small" aria-hidden="true"></span>{{ list.length ? '正在刷新' + projectLabel + '列表…' : '正在加载' + projectLabel + '列表…' }}</p>
      <div v-if="listLoading && !list.length" class="screen-project-grid" :style="gridStyle" aria-hidden="true">
        <div v-for="placeholder in Math.min(page.size, 12)" :key="placeholder" class="screen-project-skeleton"><div class="ui-skeleton"></div><span class="ui-skeleton"></span></div>
      </div>
      <div v-else-if="listError" class="screen-projects__state" role="alert">
        <i class="el-icon-warning-outline" aria-hidden="true"></i><h2>{{ projectLabel }}列表加载失败</h2><p>{{ listError }}</p><el-button size="small" @click="getList">重新加载</el-button>
      </div>
      <div v-else-if="!list.length" class="screen-projects__state" role="status">
        <i class="el-icon-monitor" aria-hidden="true"></i><h2>{{ (appliedName ? '未找到匹配的' : appliedCatalogId ? '当前分类暂无' : '暂无') + projectLabel + '项目' }}</h2>
        <p>{{ appliedCatalogId || appliedName ? '可清除筛选查看其他项目。' : '新建项目后，进入编辑器设计页面。' }}</p>
        <el-button v-if="appliedCatalogId || appliedName" size="small" @click="handleReset">清除筛选</el-button>
        <el-button v-else-if="hash(`${permission}:add`)" type="primary" size="small" @click="handleAdd">新建{{ projectLabel }}</el-button>
      </div>
      <div v-else class="screen-project-grid" :style="gridStyle" :inert="listLoading ? '' : null">
        <article v-for="(card, index) in projectCards" :key="card.id || index" class="screen-project-card" :aria-label="card.name">
          <div class="screen-project-card__cover">
            <img v-if="card.cover && !failedCoverUrls.includes(card.cover)" :src="card.cover" :alt="card.name + '封面'" loading="lazy" @error="markCoverFailed(card.cover)" />
            <div v-else class="screen-project-card__no-cover" role="img" aria-label="暂无项目封面">
              <div class="screen-project-card__placeholder-monitor" aria-hidden="true"><i class="el-icon-data-analysis"></i><i class="el-icon-pie-chart"></i><i class="el-icon-data-line"></i><i class="el-icon-copy-document"></i></div>
            </div>
            <div class="screen-project-card__cover-actions" :aria-label="card.name + '快捷操作'">
              <button v-if="hash(`${permission}:query`)" type="button" :disabled="!card.id" :aria-label="'预览' + projectLabel + '：' + card.name" title="预览" @click="handleViews(card.record)"><i class="el-icon-view" aria-hidden="true"></i></button>
              <button v-if="hash(`${permission}:add`)" type="button" :disabled="!card.id" :aria-label="'编辑' + projectLabel + '：' + card.name" title="编辑" @click="handleEdit(card.record)"><i class="el-icon-edit" aria-hidden="true"></i></button>
            </div>
            <span v-if="canQueryVersions && card.publicationLabel" class="screen-project-card__publication" :class="{ 'is-published': card.publicationLabel === '已发布', 'is-draft': card.publicationLabel === '未发布' }" role="status" :aria-label="card.name + '发布状态：' + card.publicationLabel">{{ card.publicationLabel }}</span>
          </div>
          <div class="screen-project-card__body">
            <div class="screen-project-card__actions">
              <div class="screen-project-card__heading"><h2 :title="card.name">{{ card.name }}</h2></div>
              <span class="screen-project-card__status" :class="{ 'is-enabled': card.enabled }" :title="card.status">{{ card.enabled ? '启用' : card.status }}</span>
              <div class="screen-project-card__tools">
                <button v-if="canQueryVersions" type="button" class="screen-project-card__publish" :aria-label="'发布：' + card.name" title="发布配置" @click="openVersions(card.record)"><i class="el-icon-position" aria-hidden="true"></i></button>
                <el-dropdown v-if="canManageCards" class="screen-project-card__more" trigger="click" placement="bottom-end" :disabled="!card.id" @visible-change="setCardMenu(card.id, $event)" @command="handleCardCommand($event, card.record, index)">
                  <button type="button" :disabled="!card.id" :aria-label="'更多操作：' + card.name" :aria-expanded="String(openCardMenuId === card.id)" title="更多操作" @keydown.enter.prevent><i class="el-icon-more" aria-hidden="true"></i></button>
                  <el-dropdown-menu slot="dropdown" class="screen-project-menu" role="menu" :aria-label="card.name + '更多操作'">
                    <el-dropdown-item v-if="hash(`${permission}:add`)" command="info" icon="el-icon-edit" role="menuitem" aria-label="编辑">编辑</el-dropdown-item>
                    <el-dropdown-item v-if="canCopyProject" command="copy" icon="el-icon-copy-document" role="menuitem" aria-label="复制" :disabled="!!copyLoadingId || copyUncertain">{{ copyLoadingId === card.id ? '正在复制…' : '复制' }}</el-dropdown-item>
                    <el-dropdown-item v-if="canSaveProjectTemplate" command="template" icon="el-icon-collection" role="menuitem" aria-label="存为项目模板" :disabled="templateLoadingId === card.id">存为项目模板</el-dropdown-item>
                    <el-dropdown-item v-if="hash(`${permission}:query`)" command="export" icon="el-icon-download" role="menuitem" aria-label="导出项目" :disabled="exportLoadingId === card.id">{{ exportLoadingId === card.id ? '正在导出…' : '导出项目' }}</el-dropdown-item>
                    <el-dropdown-item v-if="hash(`${permission}:add`)" command="state" :icon="card.enabled ? 'el-icon-circle-close' : 'el-icon-circle-check'" role="menuitem" :aria-label="card.enabled ? '禁用' : '启用'" :disabled="stateLoadingId === card.id">{{ stateLoadingId === card.id ? '正在更新…' : card.enabled ? '禁用' : '启用' }}</el-dropdown-item>
                    <el-dropdown-item v-if="hash(`${permission}:delete`)" command="delete" icon="el-icon-delete" role="menuitem" class="is-danger" :divided="hash(`${permission}:add`) || canSaveProjectTemplate" :aria-label="'删除大屏：' + card.name">删除</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </div>
            <p v-if="templateLoadingId === card.id" class="screen-project-card__loading" role="status"><i class="el-icon-loading" aria-hidden="true"></i>读取已保存项目…</p>
            <p v-if="copyLoadingId === card.id" class="screen-project-card__loading" role="status"><i class="el-icon-loading" aria-hidden="true"></i>{{ copySubmitting ? '正在复制全部页面…' : '等待复制确认…' }}</p>
            <p v-if="templateErrorId === card.id" class="screen-project-card__error" role="alert">{{ templateReadError }} <button type="button" @click="handleProjectTemplate(card.record)">重试</button></p>
          </div>
        </article>
      </div>
    </div>
    <footer class="screen-projects__pagination" :aria-label="projectLabel + '列表分页'">
      <span role="status">{{ listLoading ? '加载中…' : listError ? '加载失败，可重新加载' : '共 ' + page.total + ' 个项目' }}</span>
      <el-pagination v-if="!listError" layout="sizes, prev, pager, next" background :pager-count="5" :page-sizes="[12, 24, 48, 96]" :disabled="listLoading" @size-change="handleSizeChange" @current-change="handleCurrentChange" :page-size="page.size" :current-page="page.page" :total="page.total"></el-pagination>
    </footer>
    <el-dialog custom-class="management-dialog" :title="type === 'edit' ? '编辑' + projectLabel + '信息' : type === 'import' ? '导入' + projectLabel + '项目' : '新建' + projectLabel" class="screen-project-dialog" :visible.sync="box" :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="closeForm">
      <p v-if="type === 'import' && importFile" class="screen-project-dialog__hint">{{ importFile.record.name }} · {{ importFile.model.pageProject.pages.length }} 个页面。将创建独立未发布草稿，保留组件、数据源和内部跳转；图片及接口地址沿用文件配置，不上传资源文件。不继承原访问密码、临时预览身份、发布授权或版本历史。</p>
      <p v-if="type === 'import'" class="screen-project-dialog__hint">仅导入可信的项目文件。解析文件不会执行脚本，但原接口地址和数据处理会保留，后续打开编辑器或运行时可能发起请求或执行数据处理。</p>
      <el-form v-if="box" ref="projectForm" :model="form" :rules="rules" label-position="top" :disabled="saving || uncertain" @submit.native.prevent="handleSave">
        <el-form-item :label="projectLabel + '名称'" prop="name"><el-input v-model="form.name" maxlength="128" /></el-form-item>
        <el-form-item :label="projectLabel + '分类'" prop="catalogId"><el-select v-model="form.catalogId" filterable :placeholder="'选择' + projectLabel + '分类'" :loading="categoryLoading" :disabled="!!categoryError" popper-class="management-category-options"><el-option v-for="category in formCategories" :key="category.id" :label="category.label" :value="category.id" /></el-select></el-form-item>
        <el-form-item label="描述"><el-input v-model="form.description" type="textarea" maxlength="1000" /></el-form-item>
      </el-form>
      <p v-if="saveError" class="screen-project-dialog__error" role="alert">{{ saveError }}</p>
      <span slot="footer"><el-button :disabled="saving" @click="closeForm">{{ uncertain ? '关闭并检查项目列表' : '取消' }}</el-button><el-button type="primary" :loading="saving" :disabled="uncertain || categoryLoading || !!categoryError || !hash(permission + ':add') || (type === 'import' && !canImportProject)" @click="handleSave">{{ type === 'edit' ? '保存' : (type === 'import' ? '导入并编辑' : '创建并编辑') + projectLabel }}</el-button></span>
    </el-dialog>
    <el-dialog custom-class="management-dialog" :title="(categoryEditingId ? '编辑' : '新增') + projectLabel + '分类'" class="screen-project-dialog" :visible.sync="categoryBox" :close-on-click-modal="false" :close-on-press-escape="!categorySaving" :show-close="!categorySaving" :before-close="closeCategoryForm" @opened="focusCategoryName" @closed="restoreCategoryFocus">
      <p class="screen-project-dialog__hint">仅管理{{ projectLabel }}项目分类，不影响资源中心分类。</p>
      <el-form v-if="categoryBox" ref="categoryForm" :model="categoryForm" :rules="categoryRules" label-position="top" :disabled="categorySaving || categoryUncertain" @submit.native.prevent="saveCategory">
        <el-form-item label="上级分类"><el-select v-model="categoryForm.parentId" :disabled="!!categoryEditingId" clearable placeholder="一级分类" popper-class="management-category-options"><el-option v-for="category in formCategories" :key="category.id" :label="category.label" :value="category.id" /></el-select></el-form-item>
        <el-form-item label="分类名称" prop="name"><el-input ref="categoryName" v-model="categoryForm.name" maxlength="128" /></el-form-item>
        <el-form-item label="说明"><el-input v-model="categoryForm.description" type="textarea" maxlength="1000" /></el-form-item>
        <el-form-item label="排序值" prop="sortIndex"><el-input-number v-model="categoryForm.sortIndex" :min="0" :max="2147483647" :precision="0" controls-position="right" /><p class="screen-project-dialog__hint">数字越小越靠前。</p></el-form-item>
      </el-form>
      <p v-if="categorySaveError" class="screen-project-dialog__error" role="alert">{{ categorySaveError }}</p>
      <span slot="footer"><el-button :disabled="categorySaving" @click="closeCategoryForm">{{ categoryUncertain ? '关闭并检查分类' : '取消' }}</el-button><el-button type="primary" :loading="categorySaving" :disabled="categoryUncertain || !canCreateCategory" @click="saveCategory">保存分类</el-button></span>
    </el-dialog>
    <template-save-dialog v-if="templateVisible" :visible.sync="templateVisible" :snapshot="templateSnapshot" :default-name="templateName" :source-cover-file-id="templateCoverFileId" source-hint="保存已保存的全部页面，优先沿用原封面；原封面缺失或不可读时生成新封面。" @saved="onProjectTemplateSaved" />
    <project-version-dialog v-if="versionProject" :visible.sync="versionsVisible" :project-id="versionProject.id" :name="versionProject.name" :disabled="versionProject.state === 'disabled' || (versionProject.state && versionProject.state.value === 'disabled')" @changed="updatePublication" />
  </section>
</template>

<script>
import {
  addObj,
  importScreenProject,
  updateObj,
  delObj,
  copyObj,
  getObj1,
  query,
} from "@/api/visual";
import { queryTree, save as createCategory, updateOne as updateCategory, remove as deleteCategory } from "@/api/category";
import store from "@/store";
import { screenProjectCard, screenListResult, screenListError, screenCategoryOptions } from '@/utils/screen-management';
import { projectCoverFileId } from '@/utils/project-cover';
import { createRequestCancellation, isRequestCanceled } from '@/api/http';
import { templateResult } from '@/utils/templates';
import { readPageProject } from '@/schema/page-project';
import { createProjectTemplateSnapshot } from '@/editor/core/component-template';
import TemplateSaveDialog from '@/components/Templates/save-dialog.vue';
import ProjectVersionDialog from '@/components/ProjectVersions/dialog.vue';
import { projectVersionStatus } from '@/utils/project-versions';
import { createPreviewSnapshot } from '@/runtime/preview-snapshot';
import { screenCreatePayload, confirmScreenCreated, confirmScreenImported } from '@/utils/screen-create';
import { SCREEN_FILE_LIMIT, readScreenFile, importScreenPayload, exportScreenProject, downloadScreenProject } from '@/utils/screen-transfer';
import { isCanvasProjectType, projectTypeName, projectCategories, projectGridColumns } from '@/utils/project-types';
export default {
  name: "list",
  props: { projectType: { type: String, default: 'big_screen', validator: isCanvasProjectType } },
  components: { TemplateSaveDialog, ProjectVersionDialog },
  data() {
    return {
      index: 0,
      type: "",
      permission: "kydz/View/Data",
      page: {
        page: 1,
        size: 12,
        total: 0,
      },
      form: {},
      rules: { name: [{ required: true, whitespace: true, message: '请输入大屏名称', trigger: 'blur' }], catalogId: [{ required: true, message: '请选择大屏分类', trigger: 'change' }] },
      saving: false,
      uncertain: false,
      saveError: '',
      importReading: false,
      importReadId: 0,
      importFormId: 0,
      importPermissionId: 0,
      importFile: null,
      exportLoadingId: '',
      exportRequestId: 0,
      stateLoadingId: '',
      copyLoadingId: '',
      copySubmitting: false,
      copyUncertain: false,
      copyChecked: false,
      copyCheckRequestId: 0,
      copyRequestId: 0,
      box: false,
      activeName: "",
      list: [],
      categoryList: [],
      listLoading: false,
      loadedListScope: '',
      listError: '',
      listRequestId: 0,
      categoryLoading: false,
      categoryError: '',
      categoryRequestId: 0,
      appliedCatalogId: '',
      appliedName: '',
      categoryBox: false,
      categoryEditingId: '',
      gridColumns: 5,
      categorySaving: false,
      categoryUncertain: false,
      categorySaveError: '',
      categoryForm: { name: '', description: '', parentId: '', sortIndex: 50 },
      categoryRules: { name: [{ required: true, whitespace: true, message: '请输入分类名称', trigger: 'blur' }], sortIndex: [{ required: true, type: 'integer', min: 0, max: 2147483647, message: '请输入有效的非负整数', trigger: 'blur' }] },
      failedCoverUrls: [],
      openCardMenuId: '',
      templateVisible: false,
      templateSnapshot: null,
      templateCoverFileId: '',
      templateName: '',
      templateLoadingId: '',
      templateErrorId: '',
      templateReadError: '',
      templateRequestId: 0,
      publicationUpdates: {},
      versionProject: null,
      versionsVisible: false,
      search: {
        catalogId: "", // 分类ID
        name: '',
      },
    };
  },
  created() {
    this.getCategoryList();
    this.getList();
  },
  mounted () {
    this._resizeGrid = () => {
      const target = this.$refs.results
      if (target) {
        const style = window.getComputedStyle(target)
        this.gridColumns = projectGridColumns(target.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0))
      }
    }
    if (typeof ResizeObserver !== 'undefined') { this._gridObserver = new ResizeObserver(this._resizeGrid); this._gridObserver.observe(this.$refs.results) }
    else window.addEventListener('resize', this._resizeGrid)
    this._resizeGrid()
  },
  beforeDestroy () {
    if (this._gridObserver) this._gridObserver.disconnect()
    if (this._resizeGrid && typeof window !== 'undefined') window.removeEventListener('resize', this._resizeGrid)
    // 切换管理菜单后，迟到响应不能再写入已销毁的列表。
    this.listRequestId++
    this.categoryRequestId++
    this.cancelTemplateRead()
    this.exportRequestId++
    this.copyRequestId++
    this.importReadId++
    this.importFormId++
    if (this._exportCancel) this._exportCancel.cancel()
  },
  watch: {
    canReadProject (allowed) {
      if (allowed) return
      this.listRequestId++; this.exportRequestId++; this.copyRequestId++; this.importReadId++; this.importPermissionId++
      if (this._exportCancel) this._exportCancel.cancel()
      this.cancelTemplateRead(); this.list = []; this.page.total = 0; this.listLoading = false
      this.versionsVisible = false; this.templateVisible = false; this.templateSnapshot = null; this.versionProject = null
      this.openCardMenuId = ''; if (!this.saving) this.box = false
    },
    canEditProject (allowed) { if (!allowed && !this.saving) this.box = false },
    canSaveProjectTemplate (value) { if (!value) { this.cancelTemplateRead(); this.templateVisible = false } },
    canQueryVersions (value) { if (!value) this.versionsVisible = false },
    canCopyProject (value) { if (!value) this.copyRequestId++ },
    canImportProject (value) { if (!value) { this.importReadId++; this.importPermissionId++; if (this.type === 'import' && !this.saving) { this.importFormId++; this.box = false } } },
    canCreateCategory (value) { if (!value && !this.categorySaving) this.categoryBox = false }
  },
  computed: {
    canReadProject () { return this.hash(this.permission + ':query') },
    canEditProject () { return this.canReadProject && this.hash(this.permission + ':add') },
    projectLabel () { return projectTypeName(this.projectType) },
    gridStyle () { return { gridTemplateColumns: 'repeat(' + this.gridColumns + ', minmax(0, 1fr))' } },
    selectedCategory () { return this.formCategories.find(category => category.id === this.search.catalogId) },
    canDeleteCategory () { return store.getters['auth/hasResourcePermission']('visualizationCatalog:delete') && store.getters['auth/hasResourcePermission']('visualizationCatalog:query') },
    canQueryVersions () { return store.getters['auth/hasResourcePermission']('visualization:query') },
    canCopyProject () { return this.hash(this.permission + ':query') && this.hash(this.permission + ':add') },
    canImportProject () { return this.hash(this.permission + ':query') && this.hash(this.permission + ':add') },
    canCreateCategory () { return store.getters['auth/hasResourcePermission']('visualizationCatalog:save') && store.getters['auth/hasResourcePermission']('visualizationCatalog:query') },
    projectCards () { return this.list.map(screenProjectCard) },
    canSaveProjectTemplate () { return this.hash(this.permission + ':query') && store.getters['auth/hasResourcePermission']('visualizationTemplate:query') && store.getters['auth/hasResourcePermission']('visualizationTemplate:save') },
    canManageCards () { return this.hash(this.permission + ':query') || this.hash(this.permission + ':add') || this.hash(this.permission + ':delete') || this.canSaveProjectTemplate },
    formCategories () {
      return screenCategoryOptions(this.categoryList)
    },
    appliedCategoryName () {
      const category = this.formCategories.find(item => item.id === this.appliedCatalogId)
      return category ? category.label : this.appliedCatalogId
    }
  },
  methods: {
    selectCategory (id) {
      if (id && (this.categoryLoading || this.categoryError || !this.formCategories.some(category => category.id === id))) return
      this.search.catalogId = id
      this.page.page = 1
      return this.getList()
    },
    openCategoryForm (mode = 'add') {
      if (!this.canCreateCategory || this.categoryLoading || this.categoryError || this.categorySaving) return
      const selected = this.selectedCategory
      if (mode !== 'add' && !selected) return
      this.categoryEditingId = mode === 'edit' ? selected.id : ''
      this.categoryForm = mode === 'edit' ? { ...this.findCategory(selected.id) } : { name: '', description: '', parentId: mode === 'child' ? selected.id : '', sortIndex: 50 }
      if (this.categoryForm.parentId === '-1' || !this.categoryForm.parentId) this.categoryForm.parentId = ''
      this.categorySaveError = ''; this.categoryUncertain = false; this.categoryBox = true
    },
    findCategory (id, items = this.categoryList) {
      for (const item of items) { if (item.id === id) return item; const child = this.findCategory(id, item.children || []); if (child) return child }
    },
    async handleCategoryCommand (command) {
      if (command === 'edit' || command === 'child') return this.openCategoryForm(command)
      const category = this.selectedCategory
      if (command !== 'delete' || !category || !this.canDeleteCategory || this.categorySaving || this.categoryLoading || this.categoryError) return
      const record = this.findCategory(category.id)
      if (record.children && record.children.length) { this.$message.error('请先删除子分类'); return }
      try {
        await this.$confirm('删除分类“' + category.label + '”？分类中存在项目时不能删除，请先调整项目分类。', '删除' + this.projectLabel + '分类', { confirmButtonText: '删除分类', cancelButtonText: '取消', type: 'warning' })
        if (this._isDestroyed || !this.canDeleteCategory || this.categorySaving || this.search.catalogId !== category.id) return
        this.categorySaving = true
        const result = templateResult(await deleteCategory(category.id))
        if (!result || result.id !== category.id) throw new Error('删除结果未确认，请刷新分类核对')
        if (this._isDestroyed) return
        this.search.catalogId = ''; this.page.page = 1
        this.$message.success('分类已删除'); await Promise.all([this.getCategoryList(), this.getList()])
      } catch (error) {
        if (!this._isDestroyed && error !== 'cancel' && error !== 'close') { this.$message.error(screenListError(error, '分类删除失败，请刷新核对')); await this.getCategoryList() }
      } finally { if (!this._isDestroyed) this.categorySaving = false }
    },
    closeCategoryForm () {
      if (this.categorySaving) return
      const uncertain = this.categoryUncertain
      this.categoryBox = false
      if (uncertain) this.getCategoryList()
    },
    focusCategoryName () { if (this.$refs.categoryName) this.$refs.categoryName.focus() },
    restoreCategoryFocus () {
      const target = [this.$refs.categoryActions, this.$refs.addCategory, this.$refs.categories && this.$refs.categories.querySelector('button')].find(button => button && !button.disabled)
      if (target) target.focus()
    },
    async saveCategory () {
      if (!this.categoryBox || this.categorySaving || this.categoryUncertain || !this.canCreateCategory) return
      const valid = await new Promise(resolve => this.$refs.categoryForm.validate(resolve))
      if (!valid || !this.categoryBox || this._isDestroyed || this.categorySaving || this.categoryUncertain || !this.canCreateCategory) return
      const name = this.categoryForm.name.trim(), sortIndex = this.categoryForm.sortIndex, id = this.categoryEditingId, parentId = this.categoryForm.parentId || ''
      if (!name || name.length > 128 || !Number.isInteger(sortIndex) || sortIndex < 0 || sortIndex > 2147483647) { this.categorySaveError = '请填写有效的分类名称和排序值'; return }
      if (parentId && !this.formCategories.some(category => category.id === parentId)) { this.categorySaveError = '上级分类已不可用，请重新选择'; return }
      const siblings = parentId ? this.findCategory(parentId).children || [] : this.categoryList
      if (siblings.some(category => category.id !== id && category.name === name)) { this.categorySaveError = '已有同名分类，请使用其他名称'; return }
      this.categorySaving = true; this.categorySaveError = ''
      try {
        const payload = { name, sortIndex, parentId, description: this.categoryForm.description || '', projectType: this.projectType }
        const category = templateResult(await (id ? updateCategory(id, payload) : createCategory(payload)))
        if (id ? category !== true : !category || typeof category.id !== 'string' || !category.id || category.name !== name || (category.projectType || 'big_screen') !== this.projectType) { this.categoryUncertain = true; throw new Error('分类保存结果未确认，请刷新分类检查，勿重复提交') }
        if (this._isDestroyed) return
        this.categoryBox = false; this.$message.success(this.projectLabel + '分类已保存')
        await this.getCategoryList()
      } catch (error) {
        if (!this._isDestroyed) {
          if (!error.status || error.status >= 500) this.categoryUncertain = true
          this.categorySaveError = this.categoryUncertain ? '分类创建结果未确认，请关闭并检查分类，勿重复提交' : screenListError(error, '分类保存失败，请重试')
        }
      } finally { if (!this._isDestroyed) this.categorySaving = false }
    },
    openVersions (record) { if (this.canQueryVersions) { this.versionProject = record; this.versionsVisible = true } },
    updatePublication (status) {
      if (this.versionProject && this.canQueryVersions) {
        const confirmed = projectVersionStatus(status)
        const update = { publicationStatus: confirmed.publishedVersionId ? 'published' : 'unpublished' }
        if (confirmed.coverFileId) update.coverFileId = confirmed.coverFileId
        this.$set(this.publicationUpdates, this.versionProject.id, update)
        const record = this.list.find(item => item.id === this.versionProject.id)
        if (record) Object.keys(update).forEach(key => this.$set(record, key, update[key]))
      }
    },
    closeForm () {
      if (this.saving) return
      const checkImported = this.type === 'import' && this.uncertain
      this.box = false; this.importReadId++; this.importFormId++
      if (checkImported) this.getList()
    },
    chooseImport () {
      if (this.canImportProject && !this.importReading && !this.saving) this.$refs.importInput.click()
    },
    async readImport (event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file || !this.canImportProject || this.importReading || this.saving) return
      const ticket = ++this.importReadId, listId = this.listRequestId
      this.importReading = true
      try {
        if (file.size > SCREEN_FILE_LIMIT) throw new Error('项目文件不能超过10MB')
        const imported = readScreenFile(await file.text())
        if (this._isDestroyed || ticket !== this.importReadId || listId !== this.listRequestId || !this.canImportProject) return
        this.importFile = imported; this.type = 'import'; this.uncertain = false; this.saveError = ''
        const name = typeof imported.record.name === 'string' && imported.record.name ? imported.record.name : '导入大屏'
        let end = Math.min(name.length, 125)
        if (end < name.length && /[\uD800-\uDBFF]/.test(name[end - 1]) && /[\uDC00-\uDFFF]/.test(name[end])) end--
        this.form = { name: name.slice(0, end) + '-副本', catalogId: this.search.catalogId || '', description: imported.record.description || '' }
        this.importFormId++
        this.box = true
      } catch (error) { if (!this._isDestroyed && ticket === this.importReadId && listId === this.listRequestId && this.canImportProject) this.$message.error(screenListError(error, '导入文件读取失败')) }
      finally { if (!this._isDestroyed) this.importReading = false }
    },
    async handleExport (item) {
      if (!this.hash(this.permission + ':query') || !item || !item.id || this.exportLoadingId) return
      const ticket = ++this.exportRequestId
      this._exportCancel = createRequestCancellation(); this.exportLoadingId = String(item.id)
      try {
        const record = templateResult(await getObj1(item.id, { cancelToken: this._exportCancel.token }))
        if (ticket !== this.exportRequestId || this._isDestroyed || !this.hash(this.permission + ':query')) return
        if (!record || String(record.id) !== String(item.id)) throw new Error('大屏返回编号不一致，请重新加载')
        downloadScreenProject(exportScreenProject(record), record.name)
        this.$message.success('已导出大屏最新保存的全部页面')
      } catch (error) { if (ticket === this.exportRequestId && !this._isDestroyed && !isRequestCanceled(error)) this.$message.error(screenListError(error, '导出失败，请重试')) }
      finally { if (ticket === this.exportRequestId && !this._isDestroyed) this.exportLoadingId = '' }
    },
    setCardMenu (id, visible) { if (visible) this.openCardMenuId = id; else if (this.openCardMenuId === id) this.openCardMenuId = '' },
    handleCardCommand (command, record, index) {
      if (!record || !record.id) return
      if (command === 'template') return this.handleProjectTemplate(record)
      if (command === 'export') return this.handleExport(record)
      if (command === 'delete' && this.hash(this.permission + ':delete')) return this.handleDel(record)
      if (!this.hash(this.permission + ':add')) return
      if (command === 'state') return this.handleState(record)
      if (command === 'info') return this.handleUpdate(record, index)
      if (command === 'copy') return this.handleCopy(record)
    },
    cancelTemplateRead () {
      this.templateRequestId++
      if (this._templateCancel) this._templateCancel.cancel()
      this.templateLoadingId = ''; this.templateErrorId = ''; this.templateReadError = ''
    },
    async handleProjectTemplate (item) {
      if (!this.canSaveProjectTemplate || !item || !item.id || this.templateVisible) return
      this.cancelTemplateRead()
      const requestId = this.templateRequestId
      this._templateCancel = createRequestCancellation()
      this.templateLoadingId = String(item.id)
      this.templateSnapshot = null
      this.templateCoverFileId = ''
      try {
        // 列表记录可能只有摘要；重新读取已保存的整套页面，不读取其他编辑窗口草稿。
        const record = templateResult(await getObj1(item.id, { cancelToken: this._templateCancel.token }))
        if (requestId !== this.templateRequestId || this._isDestroyed || !this.canSaveProjectTemplate) return
        if (!record || String(record.id) !== String(item.id) || record.type !== this.projectType) throw new Error('项目返回的编号或类型不一致，请重新加载')
        this.templateSnapshot = createProjectTemplateSnapshot(readPageProject(record).pageProject)
        this.templateCoverFileId = projectCoverFileId(record)
        this.templateName = record.name || '未命名大屏'
        this.templateVisible = true
      } catch (error) {
        if (requestId === this.templateRequestId && !this._isDestroyed && !isRequestCanceled(error)) {
          this.templateErrorId = String(item.id); this.templateReadError = screenListError(error, '项目读取失败，请重试')
        }
      } finally { if (requestId === this.templateRequestId && !this._isDestroyed) this.templateLoadingId = '' }
    },
    onProjectTemplateSaved () { this.$message.success('项目模板已保存，可在资源中心预览、编辑或创建新大屏') },
    markCoverFailed (url) {
      if (!this.failedCoverUrls.includes(url)) this.failedCoverUrls.push(url)
    },
    handleSearch() {
      this.appliedName = this.search.name.trim();
      this.page.page = 1;
      return this.getList();
    },

    handleReset() {
      this.search.catalogId = "";
      this.search.name = ''; this.appliedName = '';
      this.page.page = 1;
      return this.getList();
    },
    hash(code) {
      return store.getters["auth/isAuth"](code);
    },
    vaildData(id) {
      const list = [];
      for (var i = 0; i < 20; i++) {
        list.push(i);
      }
      return list.includes(id);
    },
    acknowledgeCopyResult () {
      if (!this.canCopyProject || !this.copyChecked || this.listLoading || this.listError || this.copyLoadingId) return
      this.copyUncertain = false; this.copyChecked = false
    },
    async handleCopy (item) {
      if (!this.canCopyProject || !item || !item.id || this.copyLoadingId || this.copyUncertain || this._isDestroyed) return
      const requestId = ++this.copyRequestId, listRequestId = this.listRequestId, id = String(item.id)
      const current = () => !this._isDestroyed && requestId === this.copyRequestId && listRequestId === this.listRequestId && this.canCopyProject
      this.copyLoadingId = id; this.copySubmitting = false
      let submitted = false
      try {
        await this.$confirm('复制当前已保存的全部页面。副本中的页面和组件将独立生成编号，保留布局、样式、设备及图片资源绑定；副本默认未发布，不继承分享链接、发布授权和版本历史。其他窗口尚未保存的修改不会复制。', '复制大屏项目', {
          confirmButtonText: '复制项目', cancelButtonText: '取消', type: 'warning'
        })
        // 确认期间切换分类、翻页或撤销权限，不再提交旧卡片操作。
        if (!current()) return
        submitted = true; this.copySubmitting = true
        // 写请求不自动重试，也不把取消等待误当成服务端取消复制。
        const result = templateResult(await copyObj(id))
        if (result !== 1) throw new Error('复制结果未确认')
        if (!current()) return
        this.$message.success('大屏已复制，副本为未发布状态')
        await this.getList()
      } catch (error) {
        if (this._isDestroyed || (!submitted && (error === 'cancel' || error === 'close'))) return
        const status = Number(error && (error.status || error.response && error.response.status))
        if (submitted && (!status || status >= 500)) {
          // 超时或异常回包不能证明写入失败，先核对列表，再由用户解除保护。
          this.copyUncertain = true; this.copyChecked = false; this.copyCheckRequestId = this.listRequestId
          if (current()) this.$message.error('复制结果未确认，请刷新列表核对，勿重复复制')
        } else if (current()) this.$message.error(screenListError(error, '复制失败，请重试'))
      } finally {
        if (!this._isDestroyed && this.copyLoadingId === id) { this.copyLoadingId = ''; this.copySubmitting = false }
      }
    },
    handleDel(item) {
      if (!item || !item.id || !this.canReadProject || !this.hash(this.permission + ':delete')) return
      const ticket = this.listRequestId
      return this.$confirm("是否确认永久删除当前大屏？删除后无法恢复。", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          // 确认弹框不保留授权；撤权、切页或销毁后不再提交旧操作。
          if (this._isDestroyed || ticket !== this.listRequestId || !this.canReadProject || !this.hash(this.permission + ':delete')) return false
          return delObj(item.id).then(() => true)
        })
        .then(deleted => {
          if (!deleted || this._isDestroyed || !this.canReadProject) return
          this.$message.success("删除成功");
          return this.getList();
        })
        .catch(error => {
          if (error !== 'cancel' && error !== 'close') this.$message.error(screenListError(error, '删除失败，请重试'))
        });
    },
    handleAdd() {
      if (this.saving || !this.hash(this.permission + ':add')) return;
      this.importReadId++; this.importFormId++;
      this.type = "add";
      this.uncertain = false; this.saveError = ''; this.importFile = null;
      this.form = { name: '', description: '', catalogId: this.search.catalogId || '' };
      this.box = true;
    },
    handleUpdate(item, index) {
      if (this.saving || !this.hash(this.permission + ':add')) return;
      this.importReadId++; this.importFormId++;
      this.uncertain = false; this.saveError = '';
      this.type = "edit";
      this.form = { ...item, category: String(item.category || '') };
      this.box = true;
      this.index = index;
    },
    handleEdit(item) {
      if (!this.canEditProject || !item || !item.id) return
      let routeUrl = this.$router.resolve({
        name: 'build', params: { id: item.id },
      });
      window.open(routeUrl.href, "_blank");
    },
    async handleViews(item) {
      if (!this.canReadProject || !item || !item.id) return
      const ticket = this.listRequestId
      if (item.state === 'disabled' || (item.state && item.state.value === 'disabled')) { this.$message.error('该大屏已禁用，请先启用后预览'); return; }
      const tab = window.open('about:blank', '_blank');
      if (!tab) { this.$message.warning('预览窗口被浏览器拦截，请允许弹出窗口后重试'); return; }
      try {
        const record = templateResult(await getObj1(item.id));
        if (this._isDestroyed || tab.closed || ticket !== this.listRequestId || !this.canReadProject) { if (!tab.closed) tab.close(); return; }
        if (!record || record.id !== item.id || record.type !== this.projectType) throw new Error('项目返回编号或类型不一致');
        const model = readPageProject(record);
        const preview = createPreviewSnapshot(record, { ...model.metadata, pageProject: model.pageProject }, model.config, model.nodes);
        tab.sessionStorage.setItem('visual-preview:' + preview, sessionStorage.getItem('visual-preview:' + preview));
        tab.location.href = this.$router.resolve({ name: 'view', params: { id: item.id }, query: { preview } }).href;
      } catch (error) { if (!tab.closed) tab.close(); if (!this._isDestroyed) this.$message.error(screenListError(error, '预览失败，请重试')); }
    },
    async handleImportSave () {
      if (!this.box || this.type !== 'import' || !this.importFile || this.saving || this.uncertain || this._importTask || !this.canImportProject || this._isDestroyed) return
      const task = { formId: this.importFormId, permissionId: this.importPermissionId, file: this.importFile, form: this.form, listId: this.listRequestId,
        values: JSON.stringify([this.form.name, this.form.catalogId, this.form.description]) }
      this._importTask = task
      const ownsForm = () => !this._isDestroyed && this.box && this.type === 'import' && this._importTask === task &&
        this.importFormId === task.formId && this.importFile === task.file && this.form === task.form
      const current = () => ownsForm() && this.importPermissionId === task.permissionId && this.listRequestId === task.listId && this.canImportProject &&
        JSON.stringify([this.form.name, this.form.catalogId, this.form.description]) === task.values
      let submitted = false
      try {
        const valid = await new Promise(resolve => this.$refs.projectForm.validate(resolve))
        if (!valid || !current() || this.saving || this.uncertain) return
        if (this.categoryLoading || this.categoryError || !this.formCategories.some(category => category.id === this.form.catalogId)) { this.saveError = '请选择可用的大屏分类'; return }
        const payload = importScreenPayload(task.file, task.form, undefined, undefined, this.projectType)
        this.saving = true; this.saveError = ''; submitted = true
        // 后端权威生成最终身份并初始化草稿；旧服务不支持时不回退普通新增。
        const created = confirmScreenImported(await importScreenProject(payload), payload)
        if (!ownsForm()) return
        if (!current()) { this.uncertain = true; this.saveError = '大屏已导入，但列表、输入或权限已变化，请关闭并从列表核对副本，勿重复提交'; return }
        this.box = false; this.$message.success('大屏已导入，副本为未发布状态'); this.getList()
        try { await this.$router.push({ name: 'build', params: { id: created.id } }) }
        catch (error) { if (!this._isDestroyed && error && error.name !== 'NavigationDuplicated') this.$message.error('大屏已导入，请从大屏列表进入编辑器') }
      } catch (error) {
        if (ownsForm()) {
          const status = Number(error && (error.status || error.response && error.response.status))
          if (submitted && (!status || status >= 500)) this.uncertain = true
          this.saveError = this.uncertain ? '导入结果未确认，请关闭并检查大屏列表，勿重复提交' :
            [404, 405].includes(status) ? '当前后端未提供项目导入接口，请更新并重启 kaiyuan-view 后再试，不会回退普通新增' : screenListError(error, '导入失败，请检查文件或确认后端已更新')
        }
      } finally {
        if (this._importTask === task) { this._importTask = null; if (!this._isDestroyed) this.saving = false }
      }
    },
    async handleSave () {
      if (this.type === 'import') return this.handleImportSave()
      if (this.saving || this.uncertain || !this.hash(this.permission + ':add')) return
      let valid = false
      valid = await new Promise(resolve => this.$refs.projectForm.validate(resolve))
      if (!valid || !this.box || this._isDestroyed || this.saving || this.uncertain || !this.hash(this.permission + ':add')) return
      if (!this.formCategories.some(category => category.id === this.form.catalogId)) { this.saveError = '请选择可用的大屏分类'; return }
      const adding = this.type !== 'edit'
      let payload
      try {
        payload = screenCreatePayload(this.form, this.projectType)
        if (!adding) payload = { id: this.form.id, name: payload.name, catalogId: payload.catalogId, description: payload.description }
      } catch (error) { this.saveError = error.message; return }
      this.saving = true; this.saveError = ''
      let created
      try {
        const response = await (adding ? addObj(payload) : updateObj(payload))
        if (adding) { this.uncertain = true; created = confirmScreenCreated(response, this.projectType); this.uncertain = false }
        if (this._isDestroyed) return
        this.box = false; this.$message.success(adding ? '大屏已创建' : '修改成功'); this.getList()
      } catch (error) {
        if (!this._isDestroyed) {
          if (adding && (!error.status || error.status >= 500)) this.uncertain = true
          this.saveError = this.uncertain ? '创建结果未确认，请到大屏列表检查，勿重复提交' : screenListError(error, '保存失败，请重试')
        }
      } finally { if (!this._isDestroyed) this.saving = false }
      if (created && !this._isDestroyed && this.hash(this.permission + ':add')) {
        try { await this.$router.push({ name: 'build', params: { id: created.id } }) }
        catch (error) { if (error.name !== 'NavigationDuplicated') this.$message.error('大屏已创建，请从大屏列表进入编辑器') }
      }
    },
    async handleState (item) {
      if (!this.hash(this.permission + ':add') || !item || !item.id || this.stateLoadingId) return
      const enabled = item.state === 'enabled' || (item.state && item.state.value === 'enabled')
      const state = { value: enabled ? 'disabled' : 'enabled', text: enabled ? '禁用' : '启用' }
      this.stateLoadingId = String(item.id)
      try {
        const result = templateResult(await updateObj({ id: item.id, state }))
        if (!(Number.isInteger(result) && result > 0) && !(result && result.id === item.id && (result.state === state.value || (result.state && result.state.value === state.value)))) throw new Error('状态更新结果未确认，请刷新列表检查')
        if (this._isDestroyed) return
        this.$set(item, 'state', state); this.$message.success('大屏已' + state.text)
        await this.getList()
      } catch (error) { if (!this._isDestroyed) this.$message.error(screenListError(error, '状态更新失败，请重试')) }
      finally { if (!this._isDestroyed) this.stateLoadingId = '' }
    },
    handleSelect(key) {
      this.activeName = key;
      this.page.page = 1;
      this.getList();
    },
    handleCurrentChange(val) {
      this.page.page = val;
      return this.getList();
    },
    handleSizeChange(val) {
      this.page.size = val;
      this.page.page = 1;
      return this.getList();
    },
    async getList() {
      this.publicationUpdates = {}
      this.cancelTemplateRead()
      this.openCardMenuId = ''
      const requestId = ++this.listRequestId;
      if (!this.canReadProject || this._isDestroyed) { this.list = []; this.page.total = 0; this.listLoading = false; return }
      this.listLoading = true;
      this.listError = '';
      this.appliedCatalogId = this.search.catalogId;
      this.failedCoverUrls = [];
      const scope = JSON.stringify([this.projectType, this.appliedName, this.search.catalogId, this.page.page, this.page.size])
      if (scope !== this.loadedListScope) this.list = [];
      // 刷新长列表时先回到顶部，加载期间不滚动骨架，避免局部提示移出可视区域。
      if (this.$refs.results) this.$refs.results.scrollTop = 0;
      try {
        // POST JSON使用平台原生terms数组；GET的扁平编码会被忽略，导致两类项目混在一起。
        const terms = [{ column: 'type', termType: 'eq', value: this.projectType }]
        if (this.appliedName) terms.push({ column: 'name', termType: 'like', value: '%' + this.appliedName + '%' })
        if (this.search.catalogId) terms.push({ column: 'catalogId', termType: 'eq', value: this.search.catalogId })
        const response = await query({
          pageSize: this.page.size,
          pageIndex: this.page.page - 1,
          terms
        });
        if (requestId !== this.listRequestId || this._isDestroyed || !this.canReadProject) return;
        const data = screenListResult(response, this.projectType);
        this.page.total = data.total;
        const lastPage = Math.max(1, Math.ceil(data.total / this.page.size));
        if (this.page.page > lastPage) {
          this.page.page = lastPage;
          if (data.total > 0) return await this.getList();
        }
        // 已确认的本地发布操作优先于操作前发起、稍后才返回的列表快照。
        this.list = data.records.map(record => ({ ...record, ...this.publicationUpdates[record.id] }));
        this.loadedListScope = scope
        if (this.copyUncertain && requestId > this.copyCheckRequestId) this.copyChecked = true
        this.$nextTick(() => { if (requestId === this.listRequestId && this.$refs.results) this.$refs.results.scrollTop = 0 });
      } catch (error) {
        if (requestId === this.listRequestId) {
          this.listError = screenListError(error);
          if ([401, 403].includes(Number(error && (error.status || error.response && error.response.status)))) { this.list = []; this.page.total = 0 }
        }
      } finally {
        if (requestId === this.listRequestId) this.listLoading = false;
      }
    },
    async getCategoryList() {
      const requestId = ++this.categoryRequestId;
      this.categoryLoading = true;
      this.categoryError = '';
      try {
        const res = await queryTree({});
        if (requestId !== this.categoryRequestId) return;
        const data = res.data.result;
        if (!Array.isArray(data)) throw new Error('分类列表数据无效，请重试');
        screenCategoryOptions(data);
        this.categoryList = projectCategories(data, this.projectType);

      } catch (error) {
        if (requestId === this.categoryRequestId) this.categoryError = screenListError(error, '分类列表加载失败，请重试');
      } finally {
        if (requestId === this.categoryRequestId) this.categoryLoading = false;
      }
    },
  },
};
</script>
